/* SABANICA V11: componentes del header y del Home. Sin dependencias. */

/* Menú de categorías: mega menú en desktop y panel completo en móvil, con un solo DOM */
class SbMenu extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-menu-panel]');
    this.buttons = this.querySelectorAll('[data-menu-toggle]');
    this.buttons.forEach((b) => b.addEventListener('click', () => this.toggle(b)));
    this.querySelector('[data-menu-close]')?.addEventListener('click', () => this.close());
    this.addEventListener('keydown', (e) => e.key === 'Escape' && this.close());
    document.addEventListener('click', (e) => {
      if (!this.panel.hidden && !this.contains(e.target)) this.close();
    });
  }

  toggle(button) {
    this.panel.hidden ? this.open(button) : this.close();
  }

  open(button) {
    this.opener = button;
    this.panel.hidden = false;
    this.buttons.forEach((b) => b.setAttribute('aria-expanded', 'true'));
    document.documentElement.classList.toggle('sb-no-scroll', matchMedia('(max-width: 989px)').matches);
    this.panel.querySelector('a, button')?.focus({ preventScroll: true });
  }

  close() {
    if (this.panel.hidden) return;
    this.panel.hidden = true;
    this.buttons.forEach((b) => b.setAttribute('aria-expanded', 'false'));
    document.documentElement.classList.remove('sb-no-scroll');
    this.opener?.focus({ preventScroll: true });
  }
}
customElements.define('sb-menu', SbMenu);

/* En Home el buscador del header aparece cuando la franja negra sale de pantalla */
function sbHeaderSearch() {
  const header = document.querySelector('[data-sb-header]');
  const band = document.querySelector('[data-sb-search-band]');
  if (!header) return;
  if (!band || !header.hasAttribute('data-hide-search')) {
    header.classList.add('is-search');
    return;
  }
  new IntersectionObserver(([entry]) => header.classList.toggle('is-search', !entry.isIntersecting), {
    rootMargin: `-${header.offsetHeight}px 0px 0px 0px`,
  }).observe(band);
}
sbHeaderSearch();

/* Autocompletado: Predictive Search API, renderizado por sections/sb-predictive-search.liquid */
class SbSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.box = this.querySelector('[data-suggest]');
    this.clearBtn = this.querySelector('[data-clear]');
    this.cache = new Map();
    if (this.dataset.placeholderMobile && matchMedia('(max-width: 749px)').matches) {
      this.input.placeholder = this.dataset.placeholderMobile;
    }
    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('focus', () => this.box.childElementCount && this.show());
    this.input.addEventListener('keydown', (e) => this.onKey(e));
    this.clearBtn?.addEventListener('click', () => this.clear());
    document.addEventListener('click', (e) => this.contains(e.target) || this.hide());
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.hide();
        this.input.focus();
      }
    });
  }

  onInput() {
    const q = this.input.value.trim();
    if (this.clearBtn) this.clearBtn.hidden = !this.input.value;
    clearTimeout(this.timer);
    if (q.length < 2) return this.hide();
    this.timer = setTimeout(() => this.fetch(q), 200);
  }

  async fetch(q) {
    if (this.cache.has(q)) return this.render(this.cache.get(q));
    this.controller?.abort();
    this.controller = new AbortController();
    const params = new URLSearchParams({ q, section_id: 'sb-predictive-search' });
    params.set('resources[type]', 'query,collection,product');
    params.set('resources[limit]', '6');
    params.set('resources[limit_scope]', 'each');
    params.set('resources[options][fields]', 'title,product_type,variants.title,variants.sku,vendor,tag');
    try {
      const res = await fetch(`${window.routes.predictive_search_url}?${params}`, { signal: this.controller.signal });
      if (!res.ok) throw new Error(res.status);
      const html = new DOMParser().parseFromString(await res.text(), 'text/html');
      const markup = html.querySelector('[data-sb-predictive]').innerHTML;
      this.cache.set(q, markup);
      if (this.input.value.trim() === q) this.render(markup);
    } catch (e) {
      /* Si la API falla, el formulario sigue enviando a /search */
      if (e.name !== 'AbortError') this.hide();
    }
  }

  render(markup) {
    this.box.innerHTML = markup;
    this.box.classList.toggle('is-single', this.box.querySelectorAll('.sc-suggest__col').length < 2);
    this.show();
  }

  items() {
    return [...this.box.querySelectorAll('a')];
  }

  onKey(e) {
    if (this.box.hidden || !['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return;
    const items = this.items();
    const current = items.findIndex((a) => a.getAttribute('aria-selected') === 'true');
    if (e.key === 'Enter') {
      if (current >= 0) {
        e.preventDefault();
        items[current].click();
      }
      return;
    }
    e.preventDefault();
    const next = (current + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
    items.forEach((a, i) => a.setAttribute('aria-selected', i === next ? 'true' : 'false'));
    items[next].scrollIntoView({ block: 'nearest' });
  }

  show() {
    this.box.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  hide() {
    this.box.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
  }

  clear() {
    this.input.value = '';
    this.clearBtn.hidden = true;
    this.hide();
    this.input.focus();
  }
}
customElements.define('sb-search', SbSearch);

/* Galería de destacados: sin rotación automática */
class SbSlideshow extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.counter = this.querySelector('[data-counter]');
    this.index = 0;
    this.tabs.forEach((t, i) => t.addEventListener('click', () => this.go(i)));
    this.querySelectorAll('[data-prev]').forEach((b) => b.addEventListener('click', () => this.go(this.index - 1)));
    this.querySelectorAll('[data-next]').forEach((b) => b.addEventListener('click', () => this.go(this.index + 1)));
    this.querySelector('[role="tablist"]')?.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      this.go(this.index + (e.key === 'ArrowRight' ? 1 : -1));
      this.tabs[this.index].focus();
    });
    /* El editor de temas muestra el slide del bloque seleccionado */
    document.addEventListener('shopify:block:select', (e) => {
      const i = this.slides.findIndex((s) => s.dataset.blockId === e.detail.blockId);
      if (i >= 0) this.go(i);
    });
  }

  go(i) {
    const n = this.slides.length;
    this.index = (i + n) % n;
    this.slides.forEach((s, k) => (s.hidden = k !== this.index));
    this.tabs.forEach((t, k) => {
      t.setAttribute('aria-selected', k === this.index ? 'true' : 'false');
      t.tabIndex = k === this.index ? 0 : -1;
    });
    const pad = (x) => String(x).padStart(2, '0');
    if (this.counter) this.counter.textContent = `${pad(this.index + 1)} / ${pad(n)}`;
  }
}
customElements.define('sb-slideshow', SbSlideshow);

/* Categorías en móvil: de N en N. En desktop y sin JS se ven todas */
class SbPagedGrid extends HTMLElement {
  connectedCallback() {
    this.perPage = Number(this.dataset.perPage) || 4;
    this.items = [...this.querySelectorAll('.sb-cats__li')];
    this.pages = Math.ceil(this.items.length / this.perPage);
    this.final = this.querySelector('.sb-cats__final');
    this.status = this.querySelector('[data-status]');
    this.barsBox = this.querySelector('[data-bars]');
    if (this.pages < 2) return;
    this.page = 0;
    const label = this.barsBox.dataset.label;
    for (let i = 0; i < this.pages; i++) {
      const bar = document.createElement('button');
      bar.type = 'button';
      bar.className = 'sb-cats__bar';
      bar.setAttribute('aria-label', label.replace('[page]', i + 1).replace('[pages]', this.pages));
      bar.addEventListener('click', () => this.go(i));
      this.barsBox.append(bar);
    }
    this.querySelector('[data-prev]').addEventListener('click', () => this.go(this.page - 1));
    this.querySelector('[data-next]').addEventListener('click', () => this.go(this.page + 1));
    this.classList.add('is-ready');
    this.go(0, false);
  }

  go(p, scroll = true) {
    this.page = (p + this.pages) % this.pages;
    const from = this.page * this.perPage;
    const to = Math.min(from + this.perPage, this.items.length);
    this.items.forEach((li, i) => li.toggleAttribute('data-off', i < from || i >= to));
    this.final?.toggleAttribute('data-on', this.page === this.pages - 1);
    [...this.barsBox.children].forEach((b, i) => b.setAttribute('aria-current', i === this.page ? 'page' : 'false'));
    const pad = (x) => String(x).padStart(2, '0');
    this.status.textContent = this.status.dataset.template
      .replace('[from]', pad(from + 1))
      .replace('[to]', pad(to))
      .replace('[total]', this.items.length);
    if (scroll && this.getBoundingClientRect().top < 0) this.scrollIntoView({ block: 'start' });
  }
}
customElements.define('sb-paged-grid', SbPagedGrid);

/* Abierto en desktop (tarjetas) y acordeón en móvil. Sin JS todo queda abierto */
class SbResponsiveDetails extends HTMLElement {
  connectedCallback() {
    this.details = [...this.querySelectorAll('details')];
    this.mobile = matchMedia(`(max-width: ${this.dataset.max || 749}px)`);
    this.sync = () =>
      this.details.forEach((d, i) => {
        d.open = !this.mobile.matches || (i === 0 && this.dataset.first !== 'closed');
        d.querySelector('summary').tabIndex = this.mobile.matches ? 0 : -1;
      });
    this.mobile.addEventListener('change', this.sync);
    this.sync();
    this.addEventListener('click', (e) => {
      if (!this.mobile.matches && e.target.closest('summary')) e.preventDefault();
    });
  }
}
customElements.define('sb-responsive-details', SbResponsiveDetails);

/* RFQ con la búsqueda precargada: /?rfq=<texto>#rfq */
function sbRfqPrefill() {
  const q = new URLSearchParams(location.search).get('rfq');
  const note = document.querySelector('[data-rfq-prefill]');
  if (!q || !note) return;
  note.querySelector('q').textContent = q;
  note.hidden = false;
  /* Shopify Forms carga dentro de un shadow DOM: si su campo de texto aparece, se precarga */
  let tries = 0;
  const timer = setInterval(() => {
    const area = document.querySelector('.sb-rfq__form shopify-forms-embed')?.shadowRoot?.querySelector('textarea:not([name*="captcha"])');
    if (area) {
      Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value').set.call(area, q);
      area.dispatchEvent(new Event('input', { bubbles: true }));
    }
    if (area || ++tries > 20) clearInterval(timer);
  }, 250);
}
sbRfqPrefill();

/* Agregar al requerimiento sin salir de la página. Usa el carrito de Shopify y actualiza el contador */
document.addEventListener('click', async (e) => {
  const button = e.target.closest('[data-sb-add]');
  if (!button) return;
  e.preventDefault();
  const quantity = button.hasAttribute('data-sb-qty') ? Math.max(1, Number(document.querySelector('[data-qty-input]')?.value) || 1) : 1;
  const label = button.querySelector('.sb-add__t');
  const original = label.textContent;
  button.disabled = true;
  try {
    const res = await fetch(`${window.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(button.dataset.sbAdd), quantity }], sections: 'cart-icon-bubble' }),
    });
    if (!res.ok) throw new Error(res.status);
    const data = await res.json();
    const bubble = document.getElementById('cart-icon-bubble');
    const html = new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html');
    if (bubble) bubble.innerHTML = html.querySelector('.shopify-section').innerHTML;
    sbAviso(data.items?.[0], quantity);
  } catch (err) {
    label.textContent = window.sbStrings.agregarError;
  }
  setTimeout(() => {
    label.textContent = original;
    button.disabled = false;
  }, 2200);
});

/* Filtros: columna en desktop y panel inferior en móvil, con un solo DOM. Sin JS, el formulario se envía con su botón */
class SbFilters extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.panel = this.querySelector('[data-filters-panel]');
    this.backdrop = this.querySelector('.sb-fbackdrop');
    this.opener = this.querySelector('[data-filters-open]');
    this.mobile = matchMedia('(max-width: 989px)');
    this.querySelector('[data-autosubmit]')?.addEventListener('change', () => this.form.requestSubmit());
    this.opener?.addEventListener('click', () => this.toggle(true));
    this.querySelectorAll('[data-filters-close]').forEach((b) => b.addEventListener('click', () => this.toggle(false)));
    this.addEventListener('keydown', (e) => e.key === 'Escape' && this.toggle(false));
    this.form.addEventListener('change', (e) => {
      if (e.target.type !== 'checkbox') return;
      if (this.isDrawer()) this.count();
      else this.form.requestSubmit();
    });
    this.form.addEventListener('formdata', (e) => {
      if (!e.formData.get('sort_by')) e.formData.delete('sort_by');
    });
    this.querySelectorAll('[data-filter-search]').forEach((input) =>
      input.addEventListener('input', () => {
        const q = input.value.trim().toLowerCase();
        input.closest('.sc-fgroup__body').querySelectorAll('[data-filter-value]').forEach((label) => {
          label.hidden = !label.dataset.filterValue.includes(q);
        });
      })
    );
    /* Cambiar de vista conserva filtros y orden */
    this.querySelectorAll('[data-view-link]').forEach((a) => {
      const params = new URLSearchParams(location.search);
      params.delete('page');
      params.delete('view');
      if (a.dataset.viewLink) params.set('view', a.dataset.viewLink);
      const query = params.toString();
      a.href = `${location.pathname}${query ? `?${query}` : ''}`;
    });
    this.addEventListener('click', (e) => this.more(e));
  }

  isDrawer() {
    return this.mobile.matches || this.classList.contains('sb-plp--tabla');
  }

  /* En el panel los filtros se aplican al final: el botón adelanta cuántos productos quedarán */
  async count() {
    const button = this.querySelector('[data-filters-ver]');
    if (!button) return;
    const params = new URLSearchParams(new FormData(this.form));
    try {
      const res = await fetch(`${location.pathname}?${params}`);
      const html = new DOMParser().parseFromString(await res.text(), 'text/html');
      const total = Number(html.querySelector('[data-sb-total]')?.dataset.sbTotal);
      button.textContent = total === 1 ? button.dataset.uno : button.dataset.varios.replace('[n]', total);
    } catch (err) {
      /* Sin el conteo el botón sigue aplicando los filtros */
    }
  }

  toggle(open) {
    if (!this.panel) return;
    this.panel.classList.toggle('is-open', open);
    this.backdrop.hidden = !open;
    this.opener?.setAttribute('aria-expanded', open ? 'true' : 'false');
    document.documentElement.classList.toggle('sb-no-scroll', open);
    if (open) this.panel.querySelector('button')?.focus({ preventScroll: true });
    else this.opener?.focus({ preventScroll: true });
  }

  /* Ver más: agrega la página siguiente. Sin JS es un enlace normal a esa página */
  async more(e) {
    const link = e.target.closest('[data-sb-more]');
    if (!link) return;
    e.preventDefault();
    link.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(link.href);
      const html = new DOMParser().parseFromString(await res.text(), 'text/html');
      this.querySelector('[data-sb-items]').append(...html.querySelector('[data-sb-items]').children);
      this.querySelector('[data-sb-pager]').replaceWith(html.querySelector('[data-sb-pager]'));
    } catch (err) {
      location.href = link.href;
    }
  }
}
customElements.define('sb-filters', SbFilters);

/* Ficha de producto: galería, variantes, cantidad y copiar SKU. Sin selección cargada, el precio y el SKU vienen del servidor */
class SbProduct extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.track = this.querySelector('[data-track]');
    this.slides = [...this.querySelectorAll('[data-media-id]')];
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.addEventListener('click', (e) => this.onClick(e));
    this.track?.addEventListener('scroll', () => {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => this.syncGallery(), 60);
    });
    this.syncGallery();
  }

  onClick(e) {
    const t = e.target.closest('button');
    if (!t) return;
    if (t.dataset.thumb) this.goTo(Number(t.dataset.thumb));
    if (t.hasAttribute('data-prev')) this.goTo(this.current - 1);
    if (t.hasAttribute('data-next')) this.goTo(this.current + 1);
    if (t.dataset.qty) this.step(Number(t.dataset.qty));
    if (t.hasAttribute('data-copy')) this.copy(t);
    if (t.dataset.opt) this.choose(t);
  }

  goTo(index) {
    const slide = this.slides[index];
    if (slide) this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
  }

  syncGallery() {
    if (!this.track) return;
    this.current = Math.round(this.track.scrollLeft / (this.track.clientWidth || 1));
    this.thumbs.forEach((b, i) => (i === this.current ? b.setAttribute('aria-current', 'true') : b.removeAttribute('aria-current')));
    const count = this.querySelector('[data-count]');
    if (count) count.textContent = this.current + 1;
    const prev = this.querySelector('[data-prev]');
    const next = this.querySelector('[data-next]');
    if (prev) prev.disabled = this.current === 0;
    if (next) next.disabled = this.current >= this.slides.length - 1;
  }

  step(delta) {
    const input = this.querySelector('[data-qty-input]');
    input.value = Math.max(1, (Number(input.value) || 1) + delta);
  }

  async copy(button) {
    const value = button.querySelector('[data-sku]').textContent;
    try {
      await navigator.clipboard.writeText(value);
      button.classList.add('is-copied');
      setTimeout(() => button.classList.remove('is-copied'), 1500);
    } catch (err) {
      /* El SKU sigue visible para copiarlo a mano */
    }
  }

  choose(button) {
    const group = this.querySelectorAll(`[data-opt="${button.dataset.opt}"]`);
    group.forEach((b) => {
      const on = b === button;
      b.setAttribute('aria-pressed', on);
      if (b.classList.contains('sb-opt__size')) b.classList.toggle('sc-btn--secondary', !on);
    });
    const label = this.querySelector(`[data-opt-label="${button.dataset.opt}"]`);
    if (label) label.textContent = button.dataset.value;
    const selected = [...this.querySelectorAll('[data-opt][aria-pressed="true"]')];
    const variant = this.variants.find((v) => selected.every((b) => v.options[Number(b.dataset.opt)] === b.dataset.value));
    if (variant) this.apply(variant);
  }

  apply(variant) {
    const amount = (variant.price / 100).toFixed(2).split('.');
    const price = this.querySelector('[data-price]');
    if (price) price.textContent = `$${amount[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${amount[1]}`;
    const copy = this.querySelector('[data-copy]');
    if (copy) {
      copy.hidden = !variant.sku;
      copy.querySelector('[data-sku]').textContent = variant.sku || '';
    }
    this.querySelectorAll('[data-sb-add]').forEach((b) => {
      b.dataset.sbAdd = variant.id;
      b.hidden = !variant.available;
    });
    this.querySelectorAll('[data-consulta]').forEach((a) => (a.hidden = variant.available));
    this.querySelectorAll('[data-variant-id]').forEach((i) => (i.value = variant.id));
    document.querySelectorAll('[data-quote-variant]').forEach((i) => (i.value = variant.title));
    this.querySelectorAll('[data-wa]').forEach((a) => {
      const url = new URL(a.href);
      url.searchParams.set('text', url.searchParams.get('text').replace(/Variante: .*/, `Variante: ${variant.title}`));
      a.href = url.toString().replace(/\+/g, '%20');
    });
    const url = new URL(location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState(null, '', url);
    if (variant.featured_media) {
      const index = this.slides.findIndex((s) => s.dataset.mediaId === String(variant.featured_media.id));
      if (index > -1) this.goTo(index);
    }
  }
}
customElements.define('sb-product', SbProduct);

/* Aviso al agregar: arriba a la derecha, sin salir de la página */
async function sbAviso(item, quantity) {
  if (!item) return;
  const t = window.sbStrings;
  document.querySelector('.sb-aviso')?.remove();
  const cart = await (await fetch(`${window.routes.cart_url}.js`)).json();
  const box = document.createElement('div');
  box.className = 'sc sb-aviso';
  box.setAttribute('role', 'dialog');
  box.setAttribute('aria-label', t.avisoTitulo);
  const head = document.createElement('div');
  head.className = 'sb-aviso__head';
  const title = document.createElement('p');
  title.textContent = `✓ ${t.avisoTitulo}`;
  const close = document.createElement('button');
  close.className = 'sc-iconbtn';
  close.type = 'button';
  close.setAttribute('aria-label', t.cerrar);
  close.textContent = '×';
  head.append(title, close);
  const body = document.createElement('div');
  body.className = 'sb-aviso__body';
  if (item.image) {
    const img = document.createElement('img');
    img.src = `${item.image}${item.image.includes('?') ? '&' : '?'}width=112`;
    img.alt = '';
    img.width = 56;
    img.height = 56;
    body.append(img);
  }
  const text = document.createElement('span');
  const name = document.createElement('strong');
  name.textContent = item.product_title;
  const meta = document.createElement('small');
  const variant = item.variant_title ? `${item.variant_title} · ` : '';
  meta.textContent = `${variant}${(quantity === 1 ? t.avisoUnidad : t.avisoUnidades.replace('[n]', quantity))}`;
  text.append(name, meta);
  body.append(text);
  const go = document.createElement('a');
  go.className = 'sc-btn sc-btn--lg sc-btn--block';
  go.href = t.cartUrl;
  go.textContent = t.avisoVer.replace('[n]', cart.item_count);
  const keep = document.createElement('button');
  keep.className = 'sc-btn sc-btn--secondary sc-btn--block';
  keep.type = 'button';
  keep.textContent = t.avisoSeguir;
  box.append(head, body, go, keep);
  const remove = () => box.remove();
  close.addEventListener('click', remove);
  keep.addEventListener('click', remove);
  box.addEventListener('keydown', (e) => e.key === 'Escape' && remove());
  document.body.append(box);
  go.focus({ preventScroll: true });
}

/* Requerimiento: cantidades, datos en cart.attributes y envío. El carrito solo se vacía con un envío correcto */
class SbReq extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-req-form]');
    this.showSent();
    this.prefill();
    this.addEventListener('click', (e) => this.onClick(e));
    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-line-qty]')) this.setLine(e.target, Number(e.target.value) || 0);
    });
    if (!this.form) return;
    this.form.addEventListener('input', () => this.saveLater());
    /* En captura para ir antes que el captcha de Shopify: primero el flujo, después el envío normal del formulario */
    document.addEventListener(
      'submit',
      (e) => {
        if (e.target !== this.form || this.ready) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        this.send(e.submitter);
      },
      true
    );
  }

  prefill() {
    const q = new URLSearchParams(location.search).get('rfq');
    const area = this.querySelector('[data-req-rfq]');
    if (q && area && !area.value) area.value = q;
  }

  onClick(e) {
    const step = e.target.closest('[data-step]');
    const remove = e.target.closest('[data-remove]');
    if (step) {
      const input = step.closest('.sc-qty').querySelector('[data-line-qty]');
      this.setLine(input, Math.max(0, (Number(input.value) || 0) + Number(step.dataset.step)), true);
    }
    if (remove) this.setLine(remove.closest('[data-line]').querySelector('[data-line-qty]'), 0);
  }

  setLine(input, quantity, wait) {
    input.value = quantity;
    const line = Number(input.closest('[data-line]').dataset.line);
    clearTimeout(this.lineTimer);
    this.lineTimer = setTimeout(() => this.change(line, quantity), wait ? 350 : 0);
  }

  async change(line, quantity) {
    const id = this.dataset.section;
    const res = await fetch(`${window.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity, sections: `${id},cart-icon-bubble` }),
    });
    const data = await res.json();
    if (!data.item_count) return location.reload();
    const html = new DOMParser().parseFromString(data.sections[id], 'text/html');
    ['[data-req-lines]', '[data-req-sum]', '[data-req-bar]', '[data-req-sub]'].forEach((s) => {
      const fresh = html.querySelector(s);
      if (fresh) this.querySelector(s)?.replaceWith(fresh);
    });
    const bubble = document.getElementById('cart-icon-bubble');
    const count = new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html');
    if (bubble) bubble.innerHTML = count.querySelector('.shopify-section').innerHTML;
  }

  values() {
    const attributes = {};
    this.form.querySelectorAll('[data-a]').forEach((f) => {
      if (f.type !== 'radio' || f.checked) attributes[f.dataset.a] = f.value;
    });
    const note = attributes.nota || '';
    delete attributes.nota;
    return { attributes, note };
  }

  saveLater() {
    clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => this.save(), 500);
  }

  save() {
    return fetch(`${window.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(this.values()),
    });
  }

  validate() {
    this.form.querySelectorAll('.sb-req-field-err').forEach((n) => n.remove());
    this.form.querySelectorAll('[aria-invalid]').forEach((n) => n.removeAttribute('aria-invalid'));
    let first = null;
    this.form.querySelectorAll('[required]').forEach((field) => {
      const ok = field.type === 'radio' ? this.form.querySelector(`[name="${field.name}"]:checked`) : field.checkValidity();
      if (ok) return;
      const missing = field.type === 'radio' || !field.value.trim();
      const note = document.createElement('p');
      note.className = 'sb-req-field-err';
      note.id = `${field.id || 'SbReqTipo'}-err`;
      note.textContent = missing ? this.dataset.msgReq : this.dataset.msgCorreo;
      field.setAttribute('aria-invalid', 'true');
      field.setAttribute('aria-describedby', note.id);
      (field.closest('.sc-field') || field.closest('[data-req-group]')).append(note);
      first = first || field;
    });
    first?.focus();
    return !first;
  }

  summary(cart, v) {
    const money = (c) => {
      const p = (c / 100).toFixed(2).split('.');
      return `$${p[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${p[1]}`;
    };
    /* Líneas con precio a consultar: sin importe, y sin total si hay alguna */
    const consulta = (this.dataset.consulta || '').split(',').filter(Boolean);
    const aConsultar = (i) => consulta.includes(String(i.variant_id));
    const sinPrecio = cart.items.some(aConsultar);
    const lines = cart.items.map((i) => ({
      title: i.product_title,
      variant: i.product_has_only_default_variant ? '' : i.variant_title,
      sku: i.sku || '—',
      quantity: i.quantity,
      total: aConsultar(i) ? this.dataset.consultaT : money(i.final_line_price),
    }));
    const body = lines.map((l) => `· ${l.quantity} × ${l.title}${l.variant ? ` (${l.variant})` : ''} · SKU ${l.sku} · ${l.total}`).join('\n');
    const extra = v.note ? `\n\nNota: ${v.note}` : '';
    const total = sinPrecio ? this.dataset.consultaT : money(cart.total_price);
    return { lines, total, texto: `${body}\n\nTotal de referencia: ${total}${extra}` };
  }

  /* Devuelve el número, o null si el flujo no respondió: el correo sale igual */
  async toFlow(cart, v) {
    if (!this.dataset.webhook) return null;
    const a = v.attributes;
    const detalle = ['RIF', 'Ciudad de entrega', 'Fecha requerida', 'Tipo de necesidad'].filter((k) => a[k]).map((k) => `${k}: ${a[k]}`);
    if (v.note) detalle.push(`Nota: ${v.note}`);
    const res = await fetch(this.dataset.webhook, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        cliente: { nombre: a['Persona de contacto'], email: a.Correo, telefono: a['Teléfono o WhatsApp'], empresa: a.Empresa },
        items: cart.items.map((i) => ({ variant_id: i.variant_id, quantity: i.quantity, title: i.title, price: i.final_price })),
        mensaje: detalle.join(' · '),
        origen: 'tienda',
      }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.solicitud_id ? `REQ-${String(data.solicitud_id).padStart(6, '0')}` : null;
  }

  async send(submitter) {
    if (this.sending || !this.validate()) return;
    this.sending = true;
    const labels = this.querySelectorAll('[data-send-t]');
    labels.forEach((l) => {
      l.dataset.t = l.textContent;
      l.textContent = window.sbStrings.enviando;
    });
    const error = this.querySelector('[data-req-error]');
    error.hidden = true;
    let numero = null;
    try {
      await this.save();
      const cart = await (await fetch(`${window.routes.cart_url}.js`)).json();
      const v = this.values();
      const s = this.summary(cart, v);
      numero = await this.toFlow(cart, v).catch(() => null);
      const sent = { numero, correo: v.attributes.Correo, fecha: new Date().toLocaleDateString('es-VE'), lines: s.lines, total: s.total, unidades: cart.item_count };
      sessionStorage.setItem('sbReqEnviado', JSON.stringify(sent));
      this.form.querySelector('[data-c="numero"]').value = numero || '';
      this.form.querySelector('[data-c="resumen"]').value = s.texto;
      if (numero) await fetch(`${window.routes.cart_clear_url}.js`, { method: 'POST' });
      this.ready = true;
      submitter.click();
      /* Si el formulario no redirige, el flujo ya guardó la solicitud: se muestra Enviado. Sin flujo ni formulario, error */
      setTimeout(() => {
        if (numero) location.href = `${window.routes.cart_url}?enviado=1`;
        else this.fail(error, labels);
      }, 15000);
    } catch (err) {
      this.fail(error, labels);
    }
  }

  fail(error, labels) {
    sessionStorage.removeItem('sbReqEnviado');
    this.sending = false;
    this.ready = false;
    error.textContent = this.dataset.msgError;
    error.hidden = false;
    error.scrollIntoView({ block: 'center' });
    labels.forEach((l) => (l.textContent = l.dataset.t));
  }

  async showSent() {
    const params = new URLSearchParams(location.search);
    const posted = params.get('contact_posted') === 'true';
    const box = this.querySelector('[data-req-sent]');
    if (!posted && params.get('enviado') !== '1') return;
    const sent = JSON.parse(sessionStorage.getItem('sbReqEnviado') || 'null');
    if (posted && sent && !sent.numero) await fetch(`${window.routes.cart_clear_url}.js`, { method: 'POST' });
    sessionStorage.removeItem('sbReqEnviado');
    this.querySelector('[data-req-state]')?.remove();
    this.querySelector('[data-req-bar]')?.remove();
    box.hidden = false;
    if (sent) {
      const t = window.sbStrings;
      const parts = [];
      if (sent.numero) parts.push(t.numero.replace('[n]', sent.numero));
      parts.push(t.enviadoEl.replace('[f]', sent.fecha).replace('[c]', sent.correo));
      box.querySelector('[data-sent-meta]').textContent = parts.join(' · ');
      box.querySelector('[data-sent-total]').textContent = sent.total;
      box.querySelector('[data-sent-count]').textContent = `${sent.lines.length} ${this.dataset.lineas} · ${sent.unidades} ${this.dataset.unidades}`;
      const list = box.querySelector('[data-sent-lines]');
      sent.lines.forEach((l) => {
        const li = document.createElement('li');
        const a = document.createElement('span');
        const n = document.createElement('strong');
        n.textContent = l.title;
        const m = document.createElement('small');
        m.textContent = [l.variant, `SKU ${l.sku}`].filter(Boolean).join(' · ');
        a.append(n, m);
        const q = document.createElement('span');
        q.textContent = `${l.quantity}`;
        const p = document.createElement('span');
        p.textContent = l.total;
        li.append(a, q, p);
        list.append(li);
      });
      box.querySelector('[data-sent-box]').hidden = false;
    }
    box.querySelector('[data-sent-h]').focus({ preventScroll: true });
  }
}
customElements.define('sb-req', SbReq);

/* Copiar un dato de pago */
document.addEventListener('click', async (e) => {
  const button = e.target.closest('[data-copy-value]');
  if (!button) return;
  const original = button.textContent;
  try {
    await navigator.clipboard.writeText(button.dataset.copyValue);
    button.textContent = button.dataset.ok;
    setTimeout(() => (button.textContent = original), 1500);
  } catch (err) {
    /* El dato sigue visible para copiarlo a mano */
  }
});
